import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Navigation } from "../cmps/homepage/navigation";
import { FooterSection } from "../cmps/homepage/footer-section";
import { Button } from "../components/ui/button";
import { ArrowRight } from "lucide-react";

export default function AboutPage() {
  useEffect(() => {
    document.title = "About MANAGE ME | Project Oversight";
  }, []);

  return (
    <div className="homepage-root">
      <main className="relative min-h-screen overflow-x-hidden">
        <Navigation />

        <section className="relative py-24 md:py-32 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <span className="inline-flex items-center gap-3 text-sm font-mono text-foreground/60 mb-8">
                <span className="w-8 h-px bg-foreground/30" />
                About
              </span>
            </div>

            <h1 className="text-6xl md:text-7xl lg:text-8xl font-display tracking-tight leading-[0.9] mb-8">
              We supervise so
              <br />
              <span className="text-foreground/40">you can sleep.</span>
            </h1>

            <p className="text-xl text-foreground/70 leading-relaxed max-w-3xl">
              MANAGE ME exists for one reason: clients deserve to know exactly what their contractors are doing — without having to be on-site every day. We assemble independent, vetted supervisors and give them the tools to verify progress, flag risks, and protect your budget.
            </p>
          </div>
        </section>

        <section className="relative py-24 px-4 border-t border-foreground/10">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-display mb-16">Our foundation</h2>

            <div className="grid md:grid-cols-2 gap-12 mb-16">
              <div>
                <h3 className="text-2xl font-display mb-4">Our mission</h3>
                <p className="text-foreground/70 leading-relaxed">Make project failure rare.</p>
              </div>
              <div>
                <h3 className="text-2xl font-display mb-4">Our approach</h3>
                <p className="text-foreground/70 leading-relaxed">Independent eyes, transparent reports.</p>
              </div>
              <div>
                <h3 className="text-2xl font-display mb-4">Our standard</h3>
                <p className="text-foreground/70 leading-relaxed">Photographic, signed, audit-ready.</p>
              </div>
              <div>
                <h3 className="text-2xl font-display mb-4">Our people</h3>
                <p className="text-foreground/70 leading-relaxed">Domain experts. Never generalists.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="relative py-24 px-4 bg-foreground/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-display mb-8">How it started</h2>

            <p className="text-xl text-foreground/70 leading-relaxed mb-8">
              A company built from frustrated clients.
            </p>

            <p className="text-lg text-foreground/60 leading-relaxed mb-12">
              We started MANAGE ME after watching close friends lose hundreds of thousands of dollars to projects that were &ldquo;going fine&rdquo; — until they weren't. The pattern was always the same: late updates, vague photos, milestone approvals based on trust instead of evidence.
            </p>

            <p className="text-lg text-foreground/60 leading-relaxed">
              Today, MANAGE ME runs oversight on hundreds of active projects across construction, IT, energy, renovation and manufacturing. Every supervisor we deploy is vetted, domain-trained, and accountable only to the client. We don't sell software. We sell certainty.
            </p>
          </div>
        </section>

        <section className="relative py-24 px-4 border-t border-foreground/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-display mb-8">Ready to get oversight?</h2>
            <p className="text-xl text-foreground/60 mb-12 max-w-2xl mx-auto">
              Let's talk about your project and how we can protect your investment.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/auth/signup">
                <Button
                  size="lg"
                  className="bg-foreground hover:bg-foreground/90 text-background px-8 h-14 text-base rounded-full group"
                >
                  Start a project
                  <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-14 px-8 text-base rounded-full border-foreground/20 hover:bg-foreground/5"
                >
                  Contact us
                </Button>
              </Link>
            </div>
          </div>
        </section>

        <FooterSection />
      </main>
    </div>
  );
}
